import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Chip } from '@mui/joy';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import deleteIcon from '../../../../assets/svg/deleteIconRed.svg';
import { getLastActiveTime } from '../../../../assets/utils/helpers';
import getStatusOptions from '../../../../constants/statusOptions';
import { setRemoveCountryFlag } from '../../../../slices/piiRemovalSlice';
import { RootState } from '../../../../utils/store';
import { dateToString, getTimeBgColor, getTimeColor } from '../helper';

import { useTenantSlug } from '~/features/tenant/helpers';
import { useLocalizedReportTypeData } from '~/hooks/fetcher/useReportTypeData';
import AdvancedSearch from '~/pages/customer/reports/report-details/advanced-search/AdvancedSearch';
import { TenantService } from '~/services/api/tenant/endpoints';
interface ReportDetailsHeaderProps {
  isLoading: boolean;
}

const bodyText1 = {
  color: 'gray',
  fontSize: '.9rem',
};

const bodyText2 = {
  color: 'black',
  fontFamily: '"Schibsted Grotesk", sans-serif, Arial',
  fontWeight: 700, // 700 if there is bg color
  fontSize: '1rem',
};

const setText1Color = (
  isCaseDoNotPurgeEnabled: boolean,
  isCasePrivilegedEnabled: boolean,
) => {
  let color = 'gray';
  if (isCaseDoNotPurgeEnabled || isCasePrivilegedEnabled) {
    color = '#F6F6F6';
  }
  return color;
};
const setText2Color = (
  isCaseDoNotPurgeEnabled: boolean,
  isCasePrivilegedEnabled: boolean,
) => {
  let color = 'black';
  if (isCaseDoNotPurgeEnabled || isCasePrivilegedEnabled) {
    color = '#FFFFFF';
  }
  return color;
};
const setBoxBgColor = (
  isCaseDoNotPurgeEnabled: boolean,
  isCasePrivilegedEnabled: boolean,
  isLoading: boolean,
) => {
  let bgColor = 'inherit';
  if (isLoading) {
    return bgColor;
  }
  if (isCaseDoNotPurgeEnabled && isCasePrivilegedEnabled) {
    bgColor = 'linear-gradient(90deg, #F5473B 0%, #4E7AFF 100%)';
  }
  if (isCaseDoNotPurgeEnabled && !isCasePrivilegedEnabled) {
    bgColor = '#F5473B';
  } else if (!isCaseDoNotPurgeEnabled && isCasePrivilegedEnabled) {
    bgColor = '#4E7AFF';
  }
  return bgColor;
};

const setChevronTextColor = (
  isCaseDoNotPurgeEnabled: boolean,
  isCasePrivilegedEnabled: boolean,
) => {
  let color = 'var(--neutral-white-bg-400-base, #878787)';
  if (isCaseDoNotPurgeEnabled || isCasePrivilegedEnabled) {
    color = '#FFFFFF';
  }
  return color;
};

const ReportDetailsHeader = ({ isLoading }: ReportDetailsHeaderProps) => {
  const intl = useIntl();
  const slug = useTenantSlug();

  const {
    formType,
    reportType,
    status,
    firstDueDate,
    finalDueDate,
    country,
    formattedReportNumber,
    reporterLastLogin,
    isCaseDoNotPurgeEnabled,
    isCasePrivilegedEnabled,
    totalRiskScore,
  } = useSelector((state: RootState) => state.reportData.report);

  const intakeChannel = TenantService.endpoints.intakeChannels.useQuery(
    {
      slug: slug,
    },
    { skip: !slug },
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  const piiRemovalFlag = useSelector(
    (state: RootState) => state.piiRemoval.piiRemovalFlag,
  );

  const deleteCountryFlag = useSelector(
    (state: RootState) => state.piiRemoval.removeCountryFlag,
  );
  const dispatch = useDispatch();
  const statusOptions = getStatusOptions();
  const { getLocalizedReportType } = useLocalizedReportTypeData();

  useEffect(() => {
    const handleScroll = () => {
      const scroll = scrollRef.current;
      if (scroll) {
        setShowLeftScroll(scroll.scrollLeft > 0);
        setShowRightScroll(
          scroll.scrollWidth - scroll.scrollLeft > scroll.clientWidth + 1,
        );
      }
    };

    const scroll = scrollRef.current;
    if (scroll) {
      scroll.addEventListener('scroll', handleScroll);
      setShowLeftScroll(scroll.scrollLeft > 0);
      setShowRightScroll(
        scroll.scrollWidth - scroll.scrollLeft > scroll.clientWidth + 1,
      );
    }

    return () => {
      if (scroll) {
        scroll.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  //handlers

  const deleteCountryClick = () => {
    dispatch(setRemoveCountryFlag(true));
  };

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 200, behavior: 'smooth' });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '98%',
        overflowX: 'hidden',
        background: setBoxBgColor(
          isCaseDoNotPurgeEnabled,
          isCasePrivilegedEnabled,
          isLoading,
        ),
        borderRadius: '6px',
        m: '7px 5px 0px 10px',
      }}
    >
      {/* Left Stack (75%) */}
      {showLeftScroll && (
        <Stack direction='row' alignItems='center' justifyContent='center'>
          <Button
            onClick={scrollLeft}
            style={{
              display: 'flex',
              width: '60px',
              height: '71px',
              padding: '16px 16px 16px 17px',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <KeyboardDoubleArrowLeftIcon
              sx={{
                color:
                  isCaseDoNotPurgeEnabled || isCasePrivilegedEnabled
                    ? '#FFFFFF'
                    : '#181818',
              }}
            />
            <Typography
              style={{
                textTransform: 'none',
                color: setChevronTextColor(
                  isCaseDoNotPurgeEnabled,
                  isCasePrivilegedEnabled,
                ),
                fontFamily: 'Schibsted Grotesk',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: 'normal',
              }}
            >
              <FormattedMessage defaultMessage='Prev' />
            </Typography>
          </Button>
        </Stack>
      )}
      <Stack
        direction='row'
        spacing={2}
        p={2}
        pb={0}
        id='scroll'
        ref={scrollRef}
        style={{ flex: '1', position: 'relative', overflowX: 'hidden' }}
      >
        <Stack
          direction='column'
          style={{
            overflow: 'hidden',
            minWidth: '150px',
          }}
        >
          <Typography
            sx={{
              ...bodyText1,
              color: setText1Color(
                isCaseDoNotPurgeEnabled,
                isCasePrivilegedEnabled,
              ),
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            <FormattedMessage defaultMessage='Risk Score' />
          </Typography>
          <Stack sx={{ flexDirection: 'row' }}>
            {isLoading ? (
              <Skeleton width='100%' />
            ) : (
              <Chip
                color='warning'
                size='lg'
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {totalRiskScore}
              </Chip>
            )}
          </Stack>
        </Stack>
        {[
          {
            label: intl.formatMessage({ defaultMessage: 'Channel' }),
            value: intakeChannel.data?.find(
              (channel) => channel.id === formType,
            )?.name,
            width: '150px',
            color: setText2Color(
              isCaseDoNotPurgeEnabled,
              isCasePrivilegedEnabled,
            ),
            bgcolor: 'inherit',
          },
          {
            label: intl.formatMessage({ defaultMessage: 'Report Reference' }),
            value: formattedReportNumber,
            width: '150px',
            color: setText2Color(
              isCaseDoNotPurgeEnabled,
              isCasePrivilegedEnabled,
            ),
            bgcolor: 'inherit',
          },
          {
            label: intl.formatMessage({ defaultMessage: 'Report Type' }),
            value: getLocalizedReportType(reportType.name),
            width: '160px',
            color: setText2Color(
              isCaseDoNotPurgeEnabled,
              isCasePrivilegedEnabled,
            ),
            bgcolor: 'inherit',
          },
          {
            label: intl.formatMessage({ defaultMessage: 'Country' }),
            value: country?.name ?? '',
            width: '160px',
            color: setText2Color(
              isCaseDoNotPurgeEnabled,
              isCasePrivilegedEnabled,
            ),
            bgcolor: 'inherit',
          },
          {
            label: intl.formatMessage({ defaultMessage: 'Status' }),
            value: statusOptions.find((option) => option.id === status)?.name,
            width: '160px',
            color: setText2Color(
              isCaseDoNotPurgeEnabled,
              isCasePrivilegedEnabled,
            ),
            bgcolor: 'inherit',
          },
          {
            label: intl.formatMessage({ defaultMessage: 'First Due Date' }),
            value: dateToString(
              firstDueDate ??
                intl.formatMessage({ defaultMessage: 'No Due Date' }),
            ),
            width: '160px',
            color: setText2Color(
              isCaseDoNotPurgeEnabled,
              isCasePrivilegedEnabled,
            ),
            bgcolor: 'inherit',
          },
          {
            label: intl.formatMessage({ defaultMessage: 'Final Due Date' }),
            value: dateToString(finalDueDate),
            width: '160px',
            color: getTimeColor(
              finalDueDate,
              isCaseDoNotPurgeEnabled,
              isCasePrivilegedEnabled,
            ),
            bgcolor: getTimeBgColor(
              finalDueDate,
              isCaseDoNotPurgeEnabled,
              isCasePrivilegedEnabled,
            ),
          },
          {
            label: intl.formatMessage({
              defaultMessage: 'Reporter last active',
            }),
            value: getLastActiveTime(reporterLastLogin, true),
            width: '200px',
            color: setText2Color(
              isCaseDoNotPurgeEnabled,
              isCasePrivilegedEnabled,
            ),
            bgcolor: 'inherit',
          },
        ].map(({ label, value, width, color, bgcolor }, index) => (
          <Stack
            key={index}
            direction='column'
            style={{
              overflow: 'hidden',
              minWidth: `${width}`,
            }}
          >
            <Typography
              sx={{
                ...bodyText1,
                color: setText1Color(
                  isCaseDoNotPurgeEnabled,
                  isCasePrivilegedEnabled,
                ),
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
            >
              {label}
            </Typography>
            <Stack sx={{ flexDirection: 'row' }}>
              {isLoading ? (
                <Skeleton width='100%' />
              ) : (
                <Typography
                  sx={{
                    ...bodyText2,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    textDecoration:
                      label == 'Country' &&
                      deleteCountryFlag === true &&
                      piiRemovalFlag === true
                        ? 'line-through'
                        : '',
                    color: `${color}`,
                    bgcolor: `${bgcolor}`,
                    paddingX: label == 'Final Due Date' ? '5px' : '0px',
                    borderRadius: '6px',
                  }}
                >
                  {(value == null || value == '') && label == 'Country' ? (
                    <Typography
                      sx={{
                        color: '#ff0000',
                        textTransform: 'none',
                        fontSize: '16px',
                        fontWeight: 500,
                        fontFamily: 'Schibsted Grotesk',
                      }}
                    >
                      <FormattedMessage defaultMessage='Removed' />
                    </Typography>
                  ) : (
                    value
                  )}
                </Typography>
              )}
              {label == intl.formatMessage({ defaultMessage: 'Country' }) &&
              piiRemovalFlag === true &&
              value !== '' &&
              value !== null ? (
                <Button
                  onClick={deleteCountryClick}
                  disableTouchRipple
                  sx={{ width: '20px', height: '20px', marginLeft: '-15px' }}
                >
                  <img src={deleteIcon} alt='delete-icon'></img>
                </Button>
              ) : (
                ''
              )}
            </Stack>
          </Stack>
        ))}
      </Stack>
      {showRightScroll && (
        <Stack direction='row' alignItems='center' justifyContent='center'>
          <Button
            onClick={scrollRight}
            style={{
              display: 'flex',
              width: '60px',
              height: '71px',
              padding: '16px 16px 16px 17px',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <KeyboardDoubleArrowRightIcon
              sx={{
                color:
                  isCaseDoNotPurgeEnabled || isCasePrivilegedEnabled
                    ? '#FFFFFF'
                    : '#181818',
              }}
            />
            <Typography
              style={{
                textTransform: 'none',
                color: setChevronTextColor(
                  isCaseDoNotPurgeEnabled,
                  isCasePrivilegedEnabled,
                ),
                fontFamily: 'Schibsted Grotesk',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: 'normal',
              }}
            >
              <FormattedMessage defaultMessage='Next' />
            </Typography>
          </Button>
        </Stack>
      )}
      <div className='shrink-0 flex items-center'>
        <AdvancedSearch />
      </div>
    </Box>
  );
};

export default ReportDetailsHeader;
